import { useEffect } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';
import type { CallDto } from '@act/shared/data-transfer-objects';
import {
  getCallReceiverConnectedAt,
  isInteractionEligibleForHospitalizationFollowUp,
} from '@act/shared/utilities';
import { visitDateTimeHelpers } from 'utilities/dateTimeGenerator.util';

type FormWithHospitalizationFollowUp = FieldValues & {
  isHospitalizationFollowUp?: boolean;
};

/** Outcome key that allows the hospitalization follow-up checkbox. */
export const HOSPITALIZATION_FOLLOWUP_ENGAGED_OUTCOME_KEY = 'engaged';

/** Label for the hospitalization follow-up checkbox on call interactions. */
export const HOSPITALIZATION_FOLLOWUP_CALL_CHECKBOX_LABEL =
  'Hospitalization follow up call';

/** Label for the hospitalization follow-up checkbox on visit interactions. */
export const HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_LABEL =
  'Hospitalization follow up visit';

/** Info text above the hospitalization follow-up checkbox on call interactions. */
export const HOSPITALIZATION_FOLLOWUP_CALL_CHECKBOX_INFO_MESSAGE =
  'This call can qualify to close a hospitalization follow-up gap for this member.';

/** Info text above the hospitalization follow-up checkbox on visit interactions. */
export const HOSPITALIZATION_FOLLOWUP_VISIT_CHECKBOX_INFO_MESSAGE =
  'This visit can qualify to close a hospitalization follow-up gap for this member.';

export interface InteractionOutcomeOption {
  id: number;
  key: string;
}

/**
 * Returns whether the selected outcome is Engaged.
 * @param outcomeId Selected outcome id from the form.
 * @param outcomes Available interaction outcomes.
 * @returns True when Engaged is selected.
 */
export function isEngagedInteractionOutcome(
  outcomeId: number | null | undefined,
  outcomes: InteractionOutcomeOption[] | undefined
): boolean {
  if (outcomeId == null || !outcomes?.length) {
    return false;
  }

  const outcome = outcomes.find(o => o.id === outcomeId);
  return outcome?.key === HOSPITALIZATION_FOLLOWUP_ENGAGED_OUTCOME_KEY;
}

/**
 * Clears {@link isHospitalizationFollowUp} when the outcome is not Engaged.
 * @param selectedOutcomeId Selected outcome id from the form.
 * @param outcomes Available interaction outcomes.
 * @param setValue React Hook Form setter for the parent form.
 * @returns Whether the hospitalization follow-up checkbox should be enabled.
 */
export function useHospitalizationFollowUpOutcomeSync<
  TForm extends FormWithHospitalizationFollowUp,
>(
  selectedOutcomeId: number | null | undefined,
  outcomes: InteractionOutcomeOption[] | undefined,
  setValue: UseFormSetValue<TForm>
): boolean {
  const isHospitalizationFollowUpEnabled = isEngagedInteractionOutcome(
    selectedOutcomeId,
    outcomes
  );

  useEffect(() => {
    if (!isHospitalizationFollowUpEnabled) {
      setValue(
        'isHospitalizationFollowUp' as Path<TForm>,
        false as PathValue<TForm, Path<TForm>>
      );
    }
  }, [isHospitalizationFollowUpEnabled, setValue]);

  return isHospitalizationFollowUpEnabled;
}

type HospitalizationFollowUpMember = {
  hasHospitalizationToFollowUp?: boolean;
  hospitalizationFollowUpEventDate?: string | null;
};

/**
 * Clears {@link isHospitalizationFollowUp} when the interaction date predates
 * the hospitalization event or is otherwise ineligible.
 * @param member Member with follow-up gap metadata.
 * @param interactionTimestampMs Interaction time in epoch milliseconds.
 * @param setValue React Hook Form setter for the parent form.
 */
export function useHospitalizationFollowUpDateSync<
  TForm extends FormWithHospitalizationFollowUp,
>(
  member: HospitalizationFollowUpMember | null | undefined,
  interactionTimestampMs: number | undefined,
  setValue: UseFormSetValue<TForm>
): void {
  useEffect(() => {
    const isInteractionEligible =
      member?.hasHospitalizationToFollowUp &&
      interactionTimestampMs != null &&
      isInteractionEligibleForHospitalizationFollowUp(
        member.hospitalizationFollowUpEventDate,
        interactionTimestampMs
      );

    if (!isInteractionEligible) {
      setValue(
        'isHospitalizationFollowUp' as Path<TForm>,
        false as PathValue<TForm, Path<TForm>>
      );
    }
  }, [
    member?.hasHospitalizationToFollowUp,
    member?.hospitalizationFollowUpEventDate,
    interactionTimestampMs,
    setValue,
  ]);
}

type CallForFollowUpTimestamp = Pick<
  CallDto,
  'connections' | 'timeStarted' | 'timestamp'
>;

/**
 * Resolves the interaction timestamp used for hospitalization follow-up eligibility on calls.
 * @param call Call being edited or completed.
 * @returns Epoch milliseconds for day comparison, if available.
 */
export function getCallInteractionTimestampForFollowUp(
  call: CallForFollowUpTimestamp | undefined
): number | undefined {
  if (!call) {
    return undefined;
  }

  return (
    getCallReceiverConnectedAt(call.connections) ??
    call.timeStarted ??
    call.timestamp
  );
}

/**
 * Resolves the interaction timestamp used for hospitalization follow-up eligibility on visits.
 * @param visitStartTime Existing visit start time when editing.
 * @param visitDate Selected visit date when creating.
 * @param visitStart Selected start-of-day offset when creating.
 * @returns Epoch milliseconds for day comparison, if available.
 */
export function getVisitInteractionTimestampForFollowUp(
  visitStartTime: number | undefined,
  visitDate: string | undefined,
  visitStart: number | undefined
): number | undefined {
  if (visitDate) {
    const startOfVisitDay =
      visitDateTimeHelpers.epochMillisFromDateString(visitDate);

    if (startOfVisitDay == null || Number.isNaN(startOfVisitDay)) {
      return undefined;
    }

    if (visitStart != null) {
      return startOfVisitDay + visitStart;
    }

    return startOfVisitDay;
  }

  if (visitStartTime != null) {
    return visitStartTime;
  }

  return undefined;
}

/**
 * Returns whether the hospitalization follow-up checkbox should render for an interaction.
 * @param member Member with follow-up gap metadata.
 * @param interactionTimestampMs Interaction time in epoch milliseconds.
 * @returns True when the member has an open gap and the interaction is on/after it.
 */
export function shouldShowHospitalizationFollowUpCheckbox(
  member: HospitalizationFollowUpMember | null | undefined,
  interactionTimestampMs: number | undefined
): boolean {
  if (!member?.hasHospitalizationToFollowUp) {
    return false;
  }

  if (interactionTimestampMs == null) {
    return false;
  }

  return isInteractionEligibleForHospitalizationFollowUp(
    member.hospitalizationFollowUpEventDate,
    interactionTimestampMs
  );
}
